import React, { useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import { injectIntl, FormattedMessage } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { LayoutSingleColumn, Page, NamedLink, IconCard } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import { types as sdkTypes } from '../../util/sdkLoader';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import css from './WelfareIndexPage.module.css';

const { LatLng, LatLngBounds } = sdkTypes;

// Europe-wide bounds for activity browsing
const EUROPE_BOUNDS = new LatLngBounds(
  new LatLng(52.54171205, 22.87360127),
  new LatLng(35.86457571, -5.13899985)
);

// eslint-disable-next-line jsx-a11y/alt-text
const LazyImage = lazyLoadWithDimensions(props => <img {...props} />);

// ================ Sections ================ //

const HeroSection = ({ intl }) => (
  <div className={css.hero}>
    <div className={css.heroOverlay} />
    <div className={css.heroContent}>
      <p className={css.heroBadge}>
        <FormattedMessage id="WelfareIndex.heroBadge" />
      </p>
      <h1 className={css.heroTitle}>
        <FormattedMessage id="WelfareIndex.heroTitle" />
      </h1>
      <p className={css.heroSubtitle}>
        <FormattedMessage id="WelfareIndex.heroSubtitle" />
      </p>
      <div className={css.heroCtas}>
        <NamedLink name="ContactPage" className={css.heroPrimaryCta}>
          <FormattedMessage id="WelfareIndex.heroCtaPrimary" />
        </NamedLink>
        <a href="#how-it-works" className={css.heroSecondaryCta}>
          <FormattedMessage id="WelfareIndex.heroCtaSecondary" />
        </a>
      </div>
    </div>
  </div>
);

const ValuePropsCompany = ({ intl }) => {
  const props = [
    { icon: '🏔️', titleId: 'WelfareIndex.companyProp1Title', descId: 'WelfareIndex.companyProp1Desc' },
    { icon: '💚', titleId: 'WelfareIndex.companyProp2Title', descId: 'WelfareIndex.companyProp2Desc' },
    { icon: '📊', titleId: 'WelfareIndex.companyProp3Title', descId: 'WelfareIndex.companyProp3Desc' },
    { icon: '🎯', titleId: 'WelfareIndex.companyProp4Title', descId: 'WelfareIndex.companyProp4Desc' },
  ];

  return (
    <div className={css.sectionWrapper}>
      <div className={css.content}>
        <p className={css.sectionEyebrow}>
          <FormattedMessage id="WelfareIndex.forCompanies" />
        </p>
        <h2 className={css.sectionTitle}>
          <FormattedMessage id="WelfareIndex.companyValueTitle" />
        </h2>
        <div className={css.propsGrid}>
          {props.map((p, i) => (
            <div key={i} className={css.propCard}>
              <span className={css.propIcon}>{p.icon}</span>
              <h3 className={css.propTitle}>{intl.formatMessage({ id: p.titleId })}</h3>
              <p className={css.propDesc}>{intl.formatMessage({ id: p.descId })}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const ValuePropsEmployees = ({ intl }) => {
  const props = [
    { icon: '🎿', titleId: 'WelfareIndex.employeeProp1Title', descId: 'WelfareIndex.employeeProp1Desc' },
    { icon: '🧘', titleId: 'WelfareIndex.employeeProp2Title', descId: 'WelfareIndex.employeeProp2Desc' },
    { icon: '👨‍👩‍👧‍👦', titleId: 'WelfareIndex.employeeProp3Title', descId: 'WelfareIndex.employeeProp3Desc' },
    { icon: '🌳', titleId: 'WelfareIndex.employeeProp4Title', descId: 'WelfareIndex.employeeProp4Desc' },
  ];

  return (
    <div className={`${css.sectionWrapper} ${css.sectionAlt}`}>
      <div className={css.content}>
        <p className={css.sectionEyebrow}>
          <FormattedMessage id="WelfareIndex.forEmployees" />
        </p>
        <h2 className={css.sectionTitle}>
          <FormattedMessage id="WelfareIndex.employeeValueTitle" />
        </h2>
        <div className={css.propsGrid}>
          {props.map((p, i) => (
            <div key={i} className={css.propCard}>
              <span className={css.propIcon}>{p.icon}</span>
              <h3 className={css.propTitle}>{intl.formatMessage({ id: p.titleId })}</h3>
              <p className={css.propDesc}>{intl.formatMessage({ id: p.descId })}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const HowItWorksSection = ({ intl }) => {
  const steps = [
    { num: '1', titleId: 'WelfareIndex.step1Title', descId: 'WelfareIndex.step1Desc' },
    { num: '2', titleId: 'WelfareIndex.step2Title', descId: 'WelfareIndex.step2Desc' },
    { num: '3', titleId: 'WelfareIndex.step3Title', descId: 'WelfareIndex.step3Desc' },
    { num: '4', titleId: 'WelfareIndex.step4Title', descId: 'WelfareIndex.step4Desc' },
  ];

  return (
    <div id="how-it-works" className={css.sectionWrapper}>
      <div className={css.content}>
        <p className={css.sectionEyebrow}>
          <FormattedMessage id="WelfareIndex.howItWorksEyebrow" />
        </p>
        <h2 className={css.sectionTitle}>
          <FormattedMessage id="WelfareIndex.howItWorksTitle" />
        </h2>
        <div className={css.stepsGrid}>
          {steps.map((s, i) => (
            <div key={i} className={css.stepCard}>
              <div className={css.stepNumber}>{s.num}</div>
              <h3 className={css.stepTitle}>{intl.formatMessage({ id: s.titleId })}</h3>
              <p className={css.stepDesc}>{intl.formatMessage({ id: s.descId })}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const ActivitiesShowcase = ({ intl }) => {
  const categories = [
    { image: '/static/imgs/disciplines/skiing/alpineSkiing.avif', labelId: 'WelfareIndex.catSkiing' },
    { image: '/static/imgs/disciplines/climbing/rockClimbing.avif', labelId: 'WelfareIndex.catClimbing' },
    { image: '/static/imgs/disciplines/hiking/mountainHiking.avif', labelId: 'WelfareIndex.catHiking' },
    { image: '/static/imgs/disciplines/otherExperiences/yoga.avif', labelId: 'WelfareIndex.catYoga' },
    { image: '/static/imgs/disciplines/biking/mountainBiking.avif', labelId: 'WelfareIndex.catCycling' },
    { image: '/static/imgs/disciplines/lakeAndSeaSports/kayaking.avif', labelId: 'WelfareIndex.catWaterSports' },
    { image: '/static/imgs/disciplines/camping.avif', labelId: 'WelfareIndex.catCamping' },
    { image: '/static/imgs/disciplines/climbing/mountaineering.avif', labelId: 'WelfareIndex.catMountaineering' },
  ];

  return (
    <div className={`${css.sectionWrapper} ${css.sectionAlt}`}>
      <div className={css.content}>
        <p className={css.sectionEyebrow}>
          <FormattedMessage id="WelfareIndex.activitiesEyebrow" />
        </p>
        <h2 className={css.sectionTitle}>
          <FormattedMessage id="WelfareIndex.activitiesTitle" />
        </h2>
        <p className={css.sectionSubtitle}>
          <FormattedMessage id="WelfareIndex.activitiesSubtitle" />
        </p>
        <div className={css.categoriesGrid}>
          {categories.map((c, i) => (
            <div key={i} className={css.categoryCard}>
              <div className={css.categoryImageWrapper}>
                <LazyImage src={c.image} alt="" className={css.categoryImage} />
              </div>
              <span className={css.categoryLabel}>{intl.formatMessage({ id: c.labelId })}</span>
            </div>
          ))}
        </div>
        <div className={css.activitiesCtaWrapper}>
          <NamedLink
            name="SearchPage"
            to={{ search: `?bounds=${EUROPE_BOUNDS.ne.lat}%2C${EUROPE_BOUNDS.ne.lng}%2C${EUROPE_BOUNDS.sw.lat}%2C${EUROPE_BOUNDS.sw.lng}` }}
            className={css.activitiesCtaLink}
          >
            <FormattedMessage id="WelfareIndex.browseAllActivities" />
          </NamedLink>
        </div>
      </div>
    </div>
  );
};

const ImpactSection = ({ intl }) => {
  const impacts = [
    { icon: '🌿', titleId: 'WelfareIndex.impact1Title', descId: 'WelfareIndex.impact1Desc' },
    { icon: '❤️', titleId: 'WelfareIndex.impact2Title', descId: 'WelfareIndex.impact2Desc' },
    { icon: '🤝', titleId: 'WelfareIndex.impact3Title', descId: 'WelfareIndex.impact3Desc' },
  ];

  return (
    <div className={css.sectionWrapper}>
      <div className={css.content}>
        <p className={css.sectionEyebrow}>
          <FormattedMessage id="WelfareIndex.impactEyebrow" />
        </p>
        <h2 className={css.sectionTitle}>
          <FormattedMessage id="WelfareIndex.impactTitle" />
        </h2>
        <div className={css.impactGrid}>
          {impacts.map((imp, i) => (
            <div key={i} className={css.impactCard}>
              <span className={css.impactIcon}>{imp.icon}</span>
              <h3 className={css.impactCardTitle}>{intl.formatMessage({ id: imp.titleId })}</h3>
              <p className={css.impactCardDesc}>{intl.formatMessage({ id: imp.descId })}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const FaqSection = ({ intl }) => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    { q: 'WelfareIndex.faq1Q', a: 'WelfareIndex.faq1A' },
    { q: 'WelfareIndex.faq2Q', a: 'WelfareIndex.faq2A' },
    { q: 'WelfareIndex.faq3Q', a: 'WelfareIndex.faq3A' },
    { q: 'WelfareIndex.faq4Q', a: 'WelfareIndex.faq4A' },
    { q: 'WelfareIndex.faq5Q', a: 'WelfareIndex.faq5A' },
  ];

  return (
    <div className={`${css.sectionWrapper} ${css.sectionAlt}`}>
      <div className={css.content}>
        <h2 className={css.sectionTitle}>
          <FormattedMessage id="WelfareIndex.faqTitle" />
        </h2>
        <div className={css.faqAccordion}>
          {faqs.map((faq, i) => (
            <div key={i} className={css.accordionCard}>
              <h3
                className={css.accordionHead}
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
              >
                {intl.formatMessage({ id: faq.q })}
                {openIndex === i ? <IconCard brand="uparrow" /> : <IconCard brand="downarrow" />}
              </h3>
              {openIndex === i && (
                <div className={css.accordionDescriptionBox}>
                  <p className={css.faqAnswer}>{intl.formatMessage({ id: faq.a })}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const ContactCtaSection = () => (
  <div className={css.contactCta}>
    <div className={css.contactCtaContent}>
      <h2 className={css.contactCtaTitle}>
        <FormattedMessage id="WelfareIndex.contactCtaTitle" />
      </h2>
      <p className={css.contactCtaDesc}>
        <FormattedMessage id="WelfareIndex.contactCtaDesc" />
      </p>
      <NamedLink name="ContactPage" className={css.contactCtaPrimary}>
        <FormattedMessage id="WelfareIndex.contactCtaButton" />
      </NamedLink>
    </div>
  </div>
);

// ================ Main Component ================ //

const WelfareIndexPageComponent = props => {
  const { intl, scrollingDisabled } = props;

  const pageTitle = useMemo(
    () => intl.formatMessage({ id: 'WelfareIndex.pageTitle' }),
    [intl]
  );
  const pageDescription = useMemo(
    () => intl.formatMessage({ id: 'WelfareIndex.pageDescription' }),
    [intl]
  );

  return (
    <Page
      title={pageTitle}
      description={pageDescription}
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <ul className={css.sections}>
          <li className={css.section}>
            <HeroSection intl={intl} />
          </li>
          <li className={css.section}>
            <ValuePropsCompany intl={intl} />
          </li>
          <li className={css.section}>
            <ValuePropsEmployees intl={intl} />
          </li>
          <li className={css.section}>
            <HowItWorksSection intl={intl} />
          </li>
          <li className={css.section}>
            <ActivitiesShowcase intl={intl} />
          </li>
          <li className={css.section}>
            <ImpactSection intl={intl} />
          </li>
          <li className={css.section}>
            <FaqSection intl={intl} />
          </li>
          <li className={css.section}>
            <ContactCtaSection />
          </li>
        </ul>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
});

const WelfareIndexPage = compose(
  withRouter,
  connect(mapStateToProps),
  injectIntl
)(WelfareIndexPageComponent);

export default WelfareIndexPage;
